/**
 * `<Skeleton>` — foundations §9.19 primitive.
 *
 * Block-level shimmer placeholder used in chrome-skeleton loading states
 * (Batch 30j-2 introduces it for the Candidate Page; consumed in 30j-3..30j-6
 * for the AI Eval / Prep / Analysis / Notes loading scaffolds).
 *
 * Honors `prefers-reduced-motion` via the global CSS layer (foundations §7.4)
 * — `animate-pulse` automatically degrades to a static surface when the user
 * has reduced-motion set. No per-component opt-out needed.
 */
import type { JSX, CSSProperties } from "react";

type SkeletonRounded = "none" | "sm" | "md" | "lg" | "full";
type SkeletonVariant = "block" | "circle";

interface SkeletonProps {
  /** Width — number in px or any CSS length / Tailwind `w-*` utility. */
  width?: number | string;
  /** Height — number in px or any CSS length. */
  height?: number | string;
  /** Border radius preset. Maps to Tailwind `rounded-*` utilities. */
  rounded?: SkeletonRounded;
  /**
   * Visual variant. `block` (default) is a rectangular shimmer.
   * `circle` forces aspect-square + rounded-full and ignores `rounded`.
   */
  variant?: SkeletonVariant;
  /** Optional className passthrough for layout context tweaks. */
  className?: string;
}

const ROUNDED_CLASS: Record<SkeletonRounded, string> = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  full: "rounded-full",
};

function resolveDim(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value === "number") return `${value}px`;
  return value;
}

export function Skeleton({
  width,
  height,
  rounded = "md",
  variant = "block",
  className = "",
}: SkeletonProps): JSX.Element {
  const style: CSSProperties = {};
  const w = resolveDim(width);
  const h = resolveDim(height);
  if (w) style.width = w;
  if (h) style.height = h;

  const roundClass =
    variant === "circle" ? "rounded-full" : ROUNDED_CLASS[rounded];

  // Circle variant: enforce aspect-square so width=height regardless of
  // the height prop. Caller normally passes both; this keeps shape sane
  // when only one is set.
  const circleClass = variant === "circle" ? "aspect-square" : "";

  return (
    <div
      data-testid="skeleton"
      data-variant={variant}
      aria-hidden="true"
      className={`bg-surface-subtle animate-pulse ${roundClass} ${circleClass} ${className}`.trim()}
      style={style}
    />
  );
}
